import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CalendarHeart } from "lucide-react";
import { API } from "@/lib/api";
import { ideasStore } from "@/lib/storage";
import { OverallScore } from "@/components/Scores";
import { DailyPushToggle } from "@/components/DailyPushToggle";

const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long", year: "numeric" });

export default function DailyArchive() {
  const [items, setItems] = useState(null);
  const [cat, setCat] = useState("Tutte");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/daily-ideas`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  const open = (idea) => {
    ideasStore.upsert([idea]);
    navigate(`/idea/${idea.id}`);
  };

  const cats = ["Tutte", ...new Set((items || []).map((d) => d.idea.categoria))];
  const shown = (items || []).filter((d) => cat === "Tutte" || d.idea.categoria === cat);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14" data-testid="daily-archive-page">
      <div className="flex items-center gap-2 overline-label"><CalendarHeart className="w-4 h-4" /> Archivio</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4">Le idee del giorno</h1>
      <p className="text-slate-400 mt-3 max-w-xl">Ogni giorno l'AI forgia un'idea nuova. Torna domani per scoprire la prossima, o sfoglia quelle passate.</p>
      <div className="mt-6"><DailyPushToggle /></div>
      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8">
          {cats.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`archive-filter-${c.toLowerCase()}`}
              className={`rounded-full px-4 py-2 text-sm border transition-colors ${cat === c ? "bg-amber-500 border-amber-500 text-slate-950 font-semibold" : "border-white/10 text-slate-400 hover:text-slate-100"}`}>{c}</button>
          ))}
        </div>
      )}
      {items === null ? (
        <p className="text-slate-500 mt-10" data-testid="archive-loading">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="text-slate-500 mt-10" data-testid="archive-empty">Ancora nessuna idea in archivio.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
          {shown.map(({ date, idea }, i) => (
            <button key={date} onClick={() => open(idea)} data-testid={`archive-item-${date}`} style={{ animationDelay: `${i * 60}ms` }}
              className="fade-up text-left rounded-2xl bg-[#12141F] border border-white/[0.07] p-6 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-amber-500/40">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs text-slate-500 capitalize">{fmt(date)}</div>
                  <span className="inline-block mt-2 text-[11px] font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/25 rounded-full px-2.5 py-0.5">{idea.categoria}</span>
                </div>
                <OverallScore value={idea.punteggio_complessivo} testId={`archive-item-${date}-score`} />
              </div>
              <div className="font-display text-lg font-bold mt-4">{idea.nome}</div>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed line-clamp-3">{idea.pitch}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
